import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { Source } from "../lib/sseEvents";

interface SourcesPanelState {
  /** Sources currently shown in the side panel (empty when closed). */
  sources: Source[];
  isOpen: boolean;
  /** URL to highlight/scroll to when opening from a specific citation. */
  focusUrl: string | null;
  open: (sources: Source[], focusUrl?: string) => void;
  /** Open with these sources, or close if they're already showing. */
  toggle: (sources: Source[], focusUrl?: string) => void;
  close: () => void;
}

const SourcesPanelContext = createContext<SourcesPanelState | null>(null);

function sameSources(a: Source[], b: Source[]): boolean {
  return a.length === b.length && a.every((s, i) => s.url === b[i]?.url);
}

/**
 * Owns the right-hand "Sources" panel shared across the chat subtree: which
 * message's sources are shown and whether it's open. Citations and the per-turn
 * Sources button open it; the panel itself reads this state.
 */
export function SourcesPanelProvider({ children }: { children: ReactNode }) {
  const [sources, setSources] = useState<Source[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [focusUrl, setFocusUrl] = useState<string | null>(null);

  const open = useCallback((next: Source[], url?: string) => {
    setSources(next);
    setFocusUrl(url ?? null);
    setIsOpen(true);
  }, []);

  const toggle = useCallback(
    (next: Source[], url?: string) => {
      if (isOpen && sameSources(sources, next)) {
        setIsOpen(false);
        return;
      }
      setSources(next);
      setFocusUrl(url ?? null);
      setIsOpen(true);
    },
    [isOpen, sources],
  );

  const close = useCallback(() => setIsOpen(false), []);

  // The shell's right rail (reflow vs. overlay) is owned by ChatLayout's
  // RightRailController, which reconciles Sources + Canvas into one width.

  const value = useMemo<SourcesPanelState>(
    () => ({ sources, isOpen, focusUrl, open, toggle, close }),
    [sources, isOpen, focusUrl, open, toggle, close],
  );

  return <SourcesPanelContext.Provider value={value}>{children}</SourcesPanelContext.Provider>;
}

export function useSourcesPanel(): SourcesPanelState {
  const ctx = useContext(SourcesPanelContext);
  if (!ctx) throw new Error("useSourcesPanel must be used within SourcesPanelProvider");
  return ctx;
}
